import { ReactNode, Component, PureComponent, ReactElement } from 'react';
import _ from 'lodash';
import { Nav, Sidenav } from 'rsuite';
import { Icon } from '@rsuite/icons';
import * as WidgetsThingsStructs from "./../WidgetsThingsStructs";
import { IWidgetThingsClass } from '../Utils/IWidgetThingsClass';
import { BoardTheming } from '@UI/Dashboard/BoardTheming';
import { isDefined, IsNullOrEmpty, IsNullOrWhiteSpace } from '@Utils/Micro';
import { Canvas } from '@UI/Canvas/Canvas';
import { DashboardDescriptor } from '@UI/Dashboard/DashboardStructs';
import { BoardThings } from '@UI/Dashboard/BoardThings';
import { DashboardManager } from '@UI/Dashboard/DashboardManager';
import { WebThing, WebThingPort } from '@API/WebThing';
import { WebThingPortType } from '@API/WebNodeTypes';
import { Plegma } from '@API/Api';
import { WebNode } from '@API/WebNode';
import { SettingsList } from '@Primitive/SettingsList';
import { getIconClassName } from "@Primitive/IconSelectorUtils";
import { SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import {
    IWidgetDescription,
    IWidgetSettingsProps,
    WidgetDescriptionToPlegmaThing,
    WidgetType, IWidgetDataValidateResp
} from '@UI/Widgets/WidgetStructs';

import './PagePortal.less';
// =================================================================================================
export interface PagePortalData {
    WidthListThreshold: number;
}
// -------------------------------------------------------------------------------------------------
interface NavBarSetupNodeInfo {
    /** Title that we render in the list */
    Title: string;

    /** The icon for the node */
    Icon?: string;

    /** A unique key that we are going to return when the user press the node */
    Key: string;

    /** Sub Navigation nodes */
    Children?: NavBarSetupNodeInfo[];

    /** Set if the parent is open by default */
    OpenByDefault?: boolean;
}
// -------------------------------------------------------------------------------------------------
interface NavBarSetupPort {
    /** List of all nodes */
    Nodes: NavBarSetupNodeInfo[];

    /** The selected active key */
    ActiveKey?: string;

    /**
     * Disable expansion support.
     * @default false
     */
    DisableExpansion?: boolean;

    FontColor: string;
    BackgroundColor: string;
    SelectedFontColor: string;
}
// -------------------------------------------------------------------------------------------------
export interface PagePortalState {
    loadedPageId: string;
    page: DashboardDescriptor;

    // NavBarStates
    navBarExpanded: boolean;
    navBarOpenKeys: string[];
    navBarData: NavBarSetupPort;
}
// -------------------------------------------------------------------------------------------------
const enum PortKeys {
    PageId = "PageId",
    NavBarSetup = "NavBarSetup",
    NavBarClicked = "NavBarClicked"
}
// -------------------------------------------------------------------------------------------------
export class PagePortal extends Component<WidgetsThingsStructs.PartialIWidgetThingsProps<PagePortalData>, PagePortalState> {
    // ---------------------------------------------------------------------------------------------
    constructor(props: WidgetsThingsStructs.PartialIWidgetThingsProps<PagePortalData>) {
        super(props);
        this.state = {
            loadedPageId: undefined,
            page: undefined,
            navBarExpanded: true,
            navBarOpenKeys: ['3'],
            navBarData: null
        };

        let newState = this.loadFromProps(this.props);
        if (isDefined(newState))
            this.state = _.defaults(this.state, newState);
    }
    // ---------------------------------------------------------------------------------------------
    get navBarTheme(): React.CSSProperties {
        return {
            color: this.state.navBarData?.FontColor,
            backgroundColor: this.state.navBarData?.BackgroundColor
        };
    }
    // ---------------------------------------------------------------------------------------------
    componentWillUnmount() {
        // stop polling
        let page = this.state.page;
        if (isDefined(page)) {
            BoardThings.GetInstance(this.props.NodeKey)?.PulseThingStop(page);
        }
    }
    // ---------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: WidgetsThingsStructs.PartialIWidgetThingsProps<PagePortalData>) {
        if (prevProps.PortVersion != this.props.PortVersion) {
            let newState = this.loadFromProps(this.props) as PagePortalState;
            if (isDefined(newState))
                this.setState(newState);
        }
    }
    // ---------------------------------------------------------------------------------------------
    loadFromProps(nextProps: WidgetsThingsStructs.PartialIWidgetThingsProps<PagePortalData>) {
        let newState: Partial<PagePortalState> = {};

        if (_.has(nextProps.PortsStates, PortKeys.PageId)) {
            // Check for page id
            let portPageId = nextProps.PortsStates[PortKeys.PageId];
            if (portPageId?.State != this.state.loadedPageId) {
                // If we have loaded a different page id start loading process
                let board = BoardThings.GetInstance(this.props.NodeKey);
                if (isDefined(board)) {

                    // stop polling for previous page
                    let prevPage = this.state.page;
                    if (isDefined(prevPage)) {
                        board.PulseThingStop(prevPage);
                    }

                    if (!IsNullOrWhiteSpace(portPageId?.State)) {
                        let newId = portPageId.State;
                        let page = board.getPageById(newId);

                        // check if the specific page id is the page that the widget exist
                        let isNotExist = _.find(page?.Widgets, w =>
                            (w.WidgetType == WidgetType.Thing) &&
                            ((w as any).ThingKey == this.props.ThingKey)) == null;

                        if (isDefined(page) && isNotExist) {
                            // active set and start polling
                            board.UpdateActiveDashboardPortState(page.WebModuleKey);
                            board.PulseThingStart(page);

                            newState.page = page;
                            newState.loadedPageId = newId;
                        } else {
                            newState.page = null;
                            newState.loadedPageId = null;
                        }
                    }
                    else {
                        newState.page = null;
                        newState.loadedPageId = null;
                    }
                }
            }
            // Check for page id
            let navBarSetup = nextProps.PortsStates[PortKeys.NavBarSetup];
            if (_.isEmpty(navBarSetup?.State)) {
                newState.navBarData = null;
            }
            else if (!_.isEqual(navBarSetup?.State, this.state.navBarData)) {
                newState.navBarData = navBarSetup.State;
                newState.navBarExpanded = newState.navBarData?.DisableExpansion == true ? false : true;
                newState.navBarOpenKeys = [];
            }
        }
        return _.isEmpty(newState) ? null : newState;
    }
    // ---------------------------------------------------------------------------------------------
    renderCanvas(width: number, height: number) {
        const { loadedPageId, page } = this.state;
        if (!isDefined(loadedPageId) ||
            !isDefined(page))
            return null;

        let inMobileMode = width < this.props.WidgetData.WidthListThreshold;
        const boardThing = BoardThings.GetInstance(this.props.NodeKey);
        const Layout = DashboardManager.getLayout(
            page,
            false,
            inMobileMode,
            height,
            isDefined(boardThing) ? boardThing.getWidgetsVisibility() : {});

        // check if layout was found
        if (!isDefined(Layout))
            return null;

        let settings = BoardTheming.getDashboardConfiguration(this.props.NodeKey, page.Settings);
        return <Canvas
            key={this.props.key + "-canvas"}
            width={width}
            height={height}
            WidthListThreshold={this.props.WidgetData.WidthListThreshold}
            SizeW={settings.NumberOfVerticalGridSpaces}
            Layout={Layout.Layout}
            CompactLayout={settings.VerticalCompact}
            InEditMode={false}
            Margin={settings.Margin}
            BackgroundImage={settings.BackgroundImage}
            GridLayout={settings.GridLayout}
            CanvasBackgroundColor={settings.CanvasBackgroundColor}
            Padding={settings.Padding}
            ElementSettings={{
                zDepth: settings.ZDepth,
                BackgroundColor: settings.WidgetBackgroundColor,
                NoBackground: settings.WidgetNoBackground,
                BorderRadius: settings.BorderRadius,
                Outline: settings.Outline
            }}
            HeightPixels={Layout.heightPixels}
            rootStyle={{
                width: 'initial',
                height: 'initial',
                overflow: 'initial'
            }}
        />
    }
    // ---------------------------------------------------------------------------------------------
    navBarOpenChange = (newKeys: string[]) => this.setState({ navBarOpenKeys: newKeys })
    navBarOnExpand = (state: boolean) => this.setState({ navBarExpanded: state })
    // ---------------------------------------------------------------------------------------------
    onNavItemClick = (item: NavBarSetupNodeInfo) => {
        this.setState((s: PagePortalState) => {
            if (s.navBarData != null)
                s.navBarData.ActiveKey = item.Key;
            return s;
        });
        this.props.SendPortEvent(PortKeys.NavBarClicked, item);
    }
    // ---------------------------------------------------------------------------------------------
    renderNavMenu(node: NavBarSetupNodeInfo) {
        let icon = undefined;
        if (!IsNullOrWhiteSpace(node.Icon))
            icon = <Icon as={(props) => <i {...props} className={getIconClassName(node.Icon)} />} />;

        if (_.isEmpty(node.Children))
            return <Nav.Item
                eventKey={node.Key}
                icon={icon}
                onClick={() => this.onNavItemClick(node)}
                style={{
                    ...this.navBarTheme,
                    color: this.state.navBarData?.ActiveKey == node.Key
                        ? this.state.navBarData?.SelectedFontColor : 'inherit'
                }}>
                {node.Title}
            </Nav.Item>;
        else
            return <Nav.Menu
                eventKey={node.Key}
                title={node.Title}
                icon={icon}
                style={this.navBarTheme}
                menuStyle={this.navBarTheme}>
                {_.map(node.Children, x => this.renderNavMenu(x))}
            </Nav.Menu>;
    }
    // ---------------------------------------------------------------------------------------------
    renderNavBar() {
        if (_.isEmpty(this.state.navBarData?.Nodes))
            return null;

        return <Sidenav
            className='portal-sidenav'
            expanded={this.state.navBarExpanded}
            openKeys={this.state.navBarOpenKeys}
            onOpenChange={this.navBarOpenChange}
            style={{ height: '100%', ...this.navBarTheme }}>
            <Sidenav.Body style={{
                height: this.props.height - 50,
                overflow: this.state.navBarExpanded ? 'auto' : 'visible',
                ...this.navBarTheme
            }}>
                <Nav
                    activeKey={this.state.navBarData.ActiveKey}
                    style={this.navBarTheme}>
                    {_.map(this.state.navBarData.Nodes, x => this.renderNavMenu(x))}
                </Nav>
            </Sidenav.Body>
            {<Sidenav.Toggle style={this.navBarTheme} onToggle={this.navBarOnExpand} />}
        </Sidenav>;
    }
    // ---------------------------------------------------------------------------------------------
    render(): ReactNode {
        let navWidth = 0;
        const { width, height } = this.props;

        if (!_.isEmpty(this.state.navBarData?.Nodes))
            navWidth = this.state.navBarExpanded ? 240 : 56;

        return <div style={{ width, height, display: 'flex' }}>
            <div style={{ width: navWidth, height: '100%', display: 'inline-table' }}>
                {this.renderNavBar()}
            </div>
            {this.renderCanvas(width - navWidth, height)}
        </div>;
    }
}
// =================================================================================================
class PagePortalSettings extends PureComponent<IWidgetSettingsProps<PagePortalData>> {
    constructor(props: IWidgetSettingsProps<PagePortalData>) {
        super(props);
    }
    // ---------------------------------------------------------------------------------------------
    render() {
        let settings: SettingsGroup<PagePortalData>[] = [
            {
                name: "",
                elements: [
                    {
                        type: SettingsType.Slider,
                        dataPath: "WidthListThreshold",
                        label: "Width Mobile Threshold",
                        minValue: 0,
                        maxValue: 2000,
                        stepValue: 10,
                        valuePosfix: "px",
                        tooltip: "Width screen threshold in pixels. If the size less than the threshold, mobile view is enabled."
                    }
                ]
            }
        ];

        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.props.WidgetData}
            onSaveData={(data) => {
                if (isDefined(this.props.onWidgetDataUpdate))
                    this.props.onWidgetDataUpdate(data);
            }}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
}
// =================================================================================================

export class PagePortalWidget extends IWidgetThingsClass<PagePortalData> {
    Render(key: string, width: number, height: number): ReactNode {
        return <PagePortal />;
    }
    // ---------------------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): ReactElement<any> {
        const parent = this.Parent;
        return <PagePortalSettings
            key={'settings-' + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)}
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ---------------------------------------------------------------------------------------------
    private createThingPorts(): WebThingPort[] {
        let ports: WebThingPort[] = [];

        // Set open pages 
        ports.push(new WebThingPort(
            {
                Name: 'PageId',
                PortKey: PortKeys.PageId,
                Type: Plegma.ePortType.String,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ''
            },
            WebThingPortType.Data,
            (data) => this.ValidateValues('PortKey', data))
        );

        // Set Navigation info
        ports.push(new WebThingPort(
            {
                Name: 'NavBar Setup',
                PortKey: PortKeys.NavBarSetup,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ''
            },
            WebThingPortType.Data,
            (data) => this.ValidateValues('NavBarSetup', data))
        );

        // Send event when we click an event
        ports.push(new WebThingPort(
            {
                Name: 'Clicked event',
                PortKey: PortKeys.NavBarClicked,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            },
            WebThingPortType.Data));
        return ports;
    }
    // ---------------------------------------------------------------------------------------------
    updateThing() {
        let webNode = WebNode.GetInstanceKey(this.NodeKey),
            webThing = webNode.GetThing(this.ThingKey);

        // Add thing's ports.
        webThing.Ports = {};
        this.createThingPorts().forEach((port) => webThing.AddPort(port, false));

        // Update thing.
        webNode.GetModule(this.WebModuleKey).UpdateThing(webThing);
    }
    // ---------------------------------------------------------------------------------------------
    initThing() {
        // Create plegma thing.
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Create thing's ports and web thing.
        let ports = this.createThingPorts();
        let webThing = new WebThing(this.WebModule, plegmaThing, ports);

        this.ThingKey = webThing.ThingKey;
    }
    // ---------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): PagePortalData {
        return {
            WidthListThreshold: 800
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return PagePortalWidget.GetDefaultWidgetDataStatic(); }
    // ---------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        // TODO: Update the widget Description
        return {
            Title: 'Page Portal',
            WidgetTitle: '',
            Key: 'PagePortalWidget',
            Type: 'PagePortalWidget',
            Data: PagePortalWidget.GetDefaultWidgetDataStatic(),
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + '/svg/tile.svg'
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        const res = {};

        // expose title if defined
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        return res;
    }
    // ---------------------------------------------------------------------------------------------
    public ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        // todo: set schema validators
        return { valid: true, errors: "" };
    }
    // ---------------------------------------------------------------------------------------------
    public MigrateDefaultWidgetData(data: PagePortalData): PagePortalData {
        return data;
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultValues() { return {} }
    // ---------------------------------------------------------------------------------------------
}